import { useEffect, useId, useRef, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useMatches } from 'react-router-dom';

import { useAuth } from '../auth/useAuth';
import { ImpersonationBanner } from '../components/ImpersonationBanner';
import { RouteBaseProvider } from './routeBase';
import { PageChromeProvider, usePageChromeValue, type Crumb } from './pageChrome';
import { visibleNav } from './nav';
import { ContextBar } from './ContextBar';
import './AdminShell.css';

/**
 * The Admin shell.
 *
 * ── THE POINT OF IT ──────────────────────────────────────────────────────────────────
 *
 * One layout that stays mounted for the whole of an admin session. Before this, the admin
 * console was a PortalLayout like any other role portal, and the shared Event screens
 * lived only under /organizer — so "Create event" navigated the admin OUT of the admin
 * route tree, React Router unmounted the console, and the sidebar disappeared mid-task.
 *
 * The fix is routing, not components: the same EventWizard and EventManage now also mount
 * as children of this shell. There is no admin copy of either.
 *
 * ── WHAT THE SHELL OWNS ──────────────────────────────────────────────────────────────
 *
 * Grouped navigation, the header, the organization/venue context, and the breadcrumb.
 * Feature pages own their business content and nothing else. A page that wants a label in
 * the header publishes it through usePageChrome; nothing reaches into this DOM.
 */

/** Route metadata this shell understands. Set as `handle` on a route. */
export interface AdminRouteHandle {
  crumb?: string | ((params: Record<string, string | undefined>) => string);
}

function crumbsFromRoutes(
  matches: ReturnType<typeof useMatches>,
): Crumb[] {
  const crumbs: Crumb[] = [];
  for (const match of matches) {
    const handle = match.handle as AdminRouteHandle | undefined;
    if (!handle?.crumb) continue;
    const label = typeof handle.crumb === 'function'
      ? handle.crumb(match.params as Record<string, string | undefined>)
      : handle.crumb;
    crumbs.push({ label, to: match.pathname });
  }
  // The page you are on is not a link to itself.
  if (crumbs.length > 0) delete crumbs[crumbs.length - 1].to;
  return crumbs;
}

function Breadcrumbs({ crumbs }: { crumbs: Crumb[] }) {
  if (crumbs.length === 0) return null;
  return (
    <nav className="ash-crumbs" aria-label="Breadcrumb">
      <ol className="ash-crumbs__list">
        {crumbs.map((crumb, i) => (
          <li className="ash-crumbs__item" key={`${crumb.label}-${i}`}>
            {crumb.to ? (
              <Link to={crumb.to} className="ash-crumbs__link">{crumb.label}</Link>
            ) : (
              <span className="ash-crumbs__current" aria-current="page">{crumb.label}</span>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
}

function AdminShellFrame() {
  const { user, logout } = useAuth();
  const chrome = usePageChromeValue();
  const matches = useMatches();
  const location = useLocation();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const drawerRef = useRef<HTMLElement | null>(null);
  const toggleRef = useRef<HTMLButtonElement | null>(null);
  const navId = useId();

  // A drawer that survived navigation would cover the page it just opened.
  useEffect(() => { setDrawerOpen(false); }, [location.pathname, location.search]);

  // Escape closes, and focus goes back to the control that opened it rather than to the
  // top of the document.
  useEffect(() => {
    if (!drawerOpen) return;
    drawerRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setDrawerOpen(false); toggleRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const groups = visibleNav({ isPlatformAdmin: user?.role === 'admin' });

  // A page's own breadcrumb wins: it knows the entity's name, and the shell would have to
  // refetch the entity to learn it.
  const crumbs: Crumb[] = chrome.breadcrumb ?? [
    { label: 'Admin', to: '/admin' },
    ...crumbsFromRoutes(matches),
  ];

  const displayName =
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'Account';

  return (
    <div className={`admin-shell${drawerOpen ? ' is-drawer-open' : ''}`}>
      <ImpersonationBanner />
      <a className="skip-link" href="#admin-main">Skip to content</a>

      <aside
        className="ash-sidebar"
        id={navId}
        ref={drawerRef}
        tabIndex={-1}
        aria-label="Admin navigation"
      >
        <div className="ash-sidebar__head">
          <Link to="/" className="ash-sidebar__brand display">Voleto</Link>
          <p className="ash-sidebar__section">Admin</p>
        </div>

        <nav className="ash-nav">
          {groups.map((group) => (
            <div className="ash-nav__group" key={group.title}>
              <h2 className="ash-nav__title">{group.title}</h2>
              {group.items.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  className={({ isActive }) =>
                    isActive ? 'ash-nav__link is-active' : 'ash-nav__link'}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="ash-sidebar__foot">
          <p className="ash-sidebar__user" title={user?.email}>{displayName}</p>
          <p className="ash-sidebar__role identifier">{user?.role}</p>
          <button
            type="button"
            className="button button--ghost button--block"
            onClick={() => void logout()}
          >
            Sign out
          </button>
        </div>
      </aside>

      {/* Click-away for the mobile drawer. Hidden from assistive tech: Escape and the
          toggle are the real controls, and a focusable overlay would just be a trap. */}
      <div
        className="ash-scrim"
        hidden={!drawerOpen}
        onClick={() => setDrawerOpen(false)}
        aria-hidden="true"
      />

      <div className="ash-body">
        <header className="ash-header">
          <div className="ash-header__bar">
            <button
              type="button"
              ref={toggleRef}
              className="ash-menu-button"
              aria-expanded={drawerOpen}
              aria-controls={navId}
              onClick={() => setDrawerOpen((open) => !open)}
            >
              <span aria-hidden="true">☰</span>
              <span className="sr-only">
                {drawerOpen ? 'Close admin navigation' : 'Open admin navigation'}
              </span>
            </button>

            <Breadcrumbs crumbs={crumbs} />

            <ContextBar />
          </div>

          {(chrome.title || chrome.actions) && (
            <div className="ash-pagehead">
              <div className="ash-pagehead__text">
                {chrome.title && <h1 className="ash-pagehead__title display">{chrome.title}</h1>}
                {chrome.subtitle && <p className="ash-pagehead__sub">{chrome.subtitle}</p>}
              </div>
              {chrome.actions && <div className="ash-pagehead__actions">{chrome.actions}</div>}
            </div>
          )}
        </header>

        <main id="admin-main" className="ash-main">
          <div className="ash-main__inner">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}

export function AdminShell() {
  return (
    <RouteBaseProvider base="/admin">
      <PageChromeProvider>
        <AdminShellFrame />
      </PageChromeProvider>
    </RouteBaseProvider>
  );
}
